import type {
  ICopilotStreamConsumer,
  ICopilotStreamRead,
  ICopilotUIMessage,
} from "~/utils/interfaces/copilot-interfaces";

/**
 * Runs the side effects a streamed conversation carries — opening the training form on a finished
 * `reviewTraining`, opening the attendance screen on a finished `declareAttendance` — exactly once
 * each.
 *
 * A finished part is re-seen on every stream tick, so anything reacting to one needs a memory of
 * what it has already handled. That memory used to be a `Set` per consumer, written twice and reset
 * from two places; here there is one, and adding a consumer no longer means copying the pattern.
 *
 * Keys come from the *part*, never from the payload: calling the same tool again — the admin
 * correcting the session in the chat — is a new part on the same run, and has to be handled again.
 */
export const useCopilotStream = (consumers: ICopilotStreamConsumer[]) => {
  const handled = new Set<string>();

  /** Call on every `messages` change; consumers run in the order they were registered. */
  function consume(messages: ICopilotUIMessage[]) {
    for (const consumer of consumers) {
      for (const { key, payload } of consumer.read(messages)) {
        // Namespaced, so two consumers reading the same tool call cannot shadow each other.
        const handledKey = `${consumer.name}:${key}`;
        if (handled.has(handledKey)) continue;

        handled.add(handledKey);
        consumer.handle(payload);
      }
    }
  }

  /** "Nouvelle discussion" — the streamed results are gone, so nothing may replay from them. */
  function reset() {
    handled.clear();
  }

  /** Restored history is display-only; none of its outcomes may trigger a fresh side effect. */
  function prime(messages: ICopilotUIMessage[]) {
    for (const consumer of consumers) {
      for (const { key } of consumer.read(messages)) handled.add(`${consumer.name}:${key}`);
    }
  }

  return { consume, reset, prime };
};

/**
 * Declares a consumer while keeping its payload type across `read` and `handle`. The registry is
 * heterogeneous, so the single narrowing cast lives here rather than in every call site.
 */
export function copilotStreamConsumer<T>(
  name: string,
  read: (messages: ICopilotUIMessage[]) => ICopilotStreamRead<T>[],
  handle: (payload: T) => void,
): ICopilotStreamConsumer {
  return { name, read, handle: (payload) => handle(payload as T) };
}
